import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import { saveStudentRegistration } from '../studentPortalApi';
import { getPortalTheme } from '../portalTheme';
import {
  mergeGemaraSelection,
  normalizeGemaraSelection,
  summarizeGemaraSelection,
} from '../../../utils/gemaraSelection';

const SummaryStep = ({
  member,
  location,
  activePeriod,
  gemaraSelection,
  mergePrevious,
  previousPeriod,
  previousRegistration,
  onBack,
  onDone,
  mode = 'open-gemara',
  selectedExams = [],
  selectedExamIds = [],
  theme,
}) => {
  const isHalacha = mode === 'predefined';
  const t = theme || getPortalTheme(isHalacha);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const displaySelection = useMemo(() => {
    if (isHalacha) return [];
    if (mergePrevious === 'merge' && previousRegistration?.gemaraSelection?.length) {
      return mergeGemaraSelection(previousRegistration.gemaraSelection, gemaraSelection);
    }
    return normalizeGemaraSelection(gemaraSelection);
  }, [isHalacha, mergePrevious, previousRegistration, gemaraSelection]);

  const { byTractate, totalDafim } = useMemo(
    () => summarizeGemaraSelection(displaySelection),
    [displaySelection]
  );

  const handleSave = async () => {
    setError('');
    setLoading(true);
    try {
      if (isHalacha) {
        await saveStudentRegistration({
          examIds: selectedExamIds,
          location,
        });
      } else {
        await saveStudentRegistration({
          gemaraSelection,
          location,
          mergePrevious: mergePrevious === 'merge',
        });
      }
      setSaved(true);
      onDone?.();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת ההרשמה');
    } finally {
      setLoading(false);
    }
  };

  if (saved) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center', py: 2 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#7dcea0', mb: 2 }} />
        <Typography
          variant="h5"
          sx={{
            color: '#f5f0e6',
            fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
            fontWeight: 700,
            mb: 1,
          }}
        >
          ההרשמה נשמרה בהצלחה
        </Typography>
        <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 3 }}>
          תודה{member?.firstName ? ` הרב ${member.firstName}` : ''}. בהצלחה במבחנים.
        </Typography>
        <Button
          onClick={() => window.location.reload()}
          sx={{
            py: 1.2,
            px: 4,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
          }}
        >
          סיום
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
        }}
      >
        אישור הרישום
      </Typography>
      <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 3, textAlign: 'center', fontSize: '0.95rem' }}>
        בדקו את הסיכום ולחצו לאישור.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <Box
        sx={{
          border: `1px solid ${t.accentBorder}`,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
          background: t.panelBg,
        }}
      >
        {[
          { label: 'שם', value: `${member?.firstName || ''} ${member?.lastName || ''}`.trim() },
          { label: 'מועד', value: activePeriod?.name || '—' },
          { label: 'מוקד בחינה', value: location || '—' },
          !isHalacha && mergePrevious === 'merge' && previousPeriod
            ? { label: 'מיזוג', value: `כולל חומר ממועד ${previousPeriod.name}` }
            : null,
        ].filter(Boolean).map((row, i) => (
          <Box key={row.label}>
            {i > 0 && <Divider sx={{ borderColor: t.accentBorderSoft }} />}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 0.5, sm: 2 },
                px: { xs: 2, sm: 2.5 },
                py: 1.4,
              }}
            >
              <Typography sx={{ color: 'rgba(245,240,230,0.65)', fontSize: '0.9rem', flexShrink: 0 }}>{row.label}</Typography>
              <Typography sx={{ color: '#f5f0e6', fontWeight: 600, wordBreak: 'break-word', textAlign: { xs: 'start', sm: 'end' } }}>{row.value}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {isHalacha ? (
        <>
          <Typography sx={{ color: t.accentSoft, fontWeight: 700, mb: 1.5 }}>מבחנים שנבחרו</Typography>
          {(selectedExams || []).map((exam) => (
            <Box
              key={exam.id}
              sx={{
                mb: 1.2,
                px: 2,
                py: 1.4,
                borderRadius: 2,
                border: `1px solid ${t.accentBorderSoft}`,
                background: t.surfaceSoft,
              }}
            >
              <Typography sx={{ color: '#f5f0e6', fontWeight: 700 }}>
                {exam.code != null ? `${exam.code} · ` : ''}
                {exam.name}
                {exam.subject ? ` — ${exam.subject}` : ''}
              </Typography>
              <Typography sx={{ color: 'rgba(245,240,230,0.75)', fontSize: '0.9rem' }}>
                {exam.simanimText?.trim() || 'סימנים: לא הוגדרו'}
              </Typography>
            </Box>
          ))}
          <Typography sx={{ color: t.accent, textAlign: 'center', my: 2, fontWeight: 700 }}>
            סה״כ {selectedExams?.length || 0} מבחנים
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ color: t.accentSoft, fontWeight: 700, mb: 1.5 }}>
            {mergePrevious === 'merge' ? 'חומר מאוחד (קודם + נוכחי)' : 'חומר שנבחר'}
          </Typography>
          {byTractate.map((sel) => (
            <Box
              key={sel.tractate}
              sx={{
                mb: 1.2,
                px: 2,
                py: 1.4,
                borderRadius: 2,
                border: `1px solid ${t.accentBorderSoft}`,
                background: t.surfaceSoft,
              }}
            >
              <Typography sx={{ color: '#f5f0e6', fontWeight: 700 }}>{sel.tractate}</Typography>
              <Typography sx={{ color: 'rgba(245,240,230,0.75)', fontSize: '0.9rem' }}>
                {(sel.ranges || [])
                  .map((r) => `דף ${toHebrewNumeral(r.from)}–${toHebrewNumeral(r.to)}`)
                  .join(' · ')}
                {' · '}
                {sel.dafim} דף
              </Typography>
            </Box>
          ))}
          <Typography sx={{ color: t.accent, textAlign: 'center', my: 2, fontWeight: 700 }}>
            סה״כ {byTractate.length || 0} מסכתות · {totalDafim} דף
          </Typography>
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.5 }}>
        <Button
          onClick={onBack}
          disabled={loading}
          sx={{
            flex: 1,
            minHeight: 48,
            py: 1.2,
            borderRadius: 2,
            color: 'rgba(245,240,230,0.85)',
            border: `1px solid ${t.accentBorder}`,
          }}
        >
          חזרה
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          sx={{
            flex: 2,
            minHeight: 48,
            py: 1.2,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
            '&:hover': { filter: 'brightness(1.06)' },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: t.ctaColor }} /> : 'אישור ושמירה'}
        </Button>
      </Box>
    </Box>
  );
};

export default SummaryStep;
